<template>
  <div :key="`product-${reportingStore.isSidebarShown}`" class="h-100 d-flex flex-column">
    <ReportingSection :short-view="shortView">
      <template #header>
        <ReportingHeader :short-view="shortView" :section="section">
          <template #toolbar>
            <div class="d-flex flex-row justify-end flex-grow-1">
              <v-btn secondary variant="flat" @click="excelExport()">
                {{ t('reportingPage.exportCurrentView') }}
                <v-icon primary size="20" class="ml-1"> mdi-swap-vertical </v-icon>
              </v-btn>

              <v-btn
                v-show="showLayouts"
                secondary
                variant="flat"
                @click="toggleSideBar('layouts')"
              >
                {{ tkey('sideBar.toolPanels.layouts.title') }}
                <v-icon primary size="20" class="ml-1"> mdi-view-dashboard-outline </v-icon>
              </v-btn>
              &nbsp;
              <v-btn secondary variant="flat" @click="toggleSideBar('columns')">
                {{ tkey('sideBar.toolPanels.columns') }}
                <v-icon primary size="20" class="ml-1"> mdi-view-column </v-icon>
              </v-btn>
            </div>
          </template>
        </ReportingHeader>
      </template>

      <template #main-section>
        <ReportingMainSection :section="section" :short-view="shortView">
          <template #data-section>
            <div class="d-flex w-100 h-100">
              <div v-if="!canViewProductReport" class="ma-auto no-data">
                {{ t('reportingPage.noPermissions') }}
              </div>
              <BaseProgressBar
                v-else-if="isTableLoading"
                :message="t('reportingPage.loadingTable')"
                style="margin: auto"
                class="pt-5"
              />
              <AgGridVue
                v-else
                animate-rows
                dense
                class="w-100 h-100"
                :column-defs="columnDefs"
                :row-data="tableData"
                :grid-options="gridOptions"
                :side-bar="sideBar"
                :excel-styles="excelStyles"
                @grid-ready="onGridReady"
              />
            </div>
          </template>
        </ReportingMainSection>
      </template>
    </ReportingSection>
    <!-- confirmation dialog -->
    <v-dialog
      v-model="isConfirmDialogOpen"
      retain-focus
      max-width="450"
      content-class="rtls-dialog"
      @keydown.esc="closeConfirmExportDialog"
      @click:outside="closeConfirmExportDialog"
    >
      <BaseDialogCard
        ref="confirm"
        :title="t('general.warning')"
        :border="true"
        @close="closeDeleteNewProductsDialog"
      >
        <v-card-text class="text-h6">
          <p>{{ tkey('confirmationDialog.warning') }}</p>
        </v-card-text>
        <template #footer>
          <v-card-actions class="justify-center">
            <div class="d-flex justify-end px-0">
              <v-btn primary @click="exportToExcel">
                {{ t('actions.proceed') }}
              </v-btn>
              <v-btn variant="flat" class="cancel" @click="closeConfirmExportDialog">
                {{ t('actions.cancel') }}
              </v-btn>
            </div>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>
  </div>
</template>

<script>
import {
  differenceBy,
  flatten,
  isEmpty,
  groupBy,
  isArray,
  sortBy,
  forEach,
  has,
  get,
  keyBy,
  head,
} from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// utils and plugins
import agGridUtils from '@/utils/ag-grid-utils';

// enums
import reportingSections from '@enums/reporting-sections';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';
import { useFormatNumber } from '@/composables/use-format-number';
import { useProductIndicators } from '@/composables/product-indicators';

// stores
import { useReportingStore } from '@/stores/reporting';
import { useContextStore } from '@/stores/context';
import { useSnackbarStore } from '@/stores/snackbar';

const groupsOrderMap = {
  details: 1,
  financials: 2,
  other: 3,
  cluster: 4,
  chain: 5,
  inputRanking: 6,
  placement: 7,
  outputRanking: 8,
  intermediateOutputRanking: 9,
  settingUser: 10,
  customAttributes: 11,
};

const fieldEnum = {
  isNewProduct: 'isNewProduct',
  productType: 'productType',
};

dayjs.extend(utc);

export default {
  props: {
    // represents whether this report is open on (short) overview page
    shortView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const { t, tkey } = useInternationalization('reportingPage.sections.product');
    const { canViewProductReport } = usePermission();
    const { formatNumber } = useFormatNumber();
    const { showLayouts } = useFeature();

    const { getProductTypeDisplayString } = useProductIndicators();

    const reportingStore = useReportingStore();
    const contextStore = useContextStore();
    const snackbarStore = useSnackbarStore();

    return {
      // i18n
      t,
      tkey,
      // permissions
      canViewProductReport,
      // features
      showLayouts,
      // formatters
      formatNumber,
      // stores
      reportingStore,
      contextStore,
      snackbarStore,
      // product indicators
      getProductTypeDisplayString,
    };
  },

  data() {
    return {
      reportingSections,
      tableData: [],
      excelStyles: null,
      overviewData: [],
      columnsConfig: [],
      sideBar: null,
      section: reportingSections.product,
      gridApi: null,
      isExporting: false,
      isConfirmDialogOpen: false,
    };
  },

  computed: {
    gridOptions() {
      return {
        columnMenu: 'legacy',
        rowHeight: 30,
        floatingFiltersHeight: 45,
        headerHeight: 55,
        suppressContextMenu: true,
        cellSelection: { handle: { mode: 'range' } },

        // TODO remove - this is deprecated in AG Grid v33
        // this was added to allow custom attributes in columnDefs or gridOptions
        // Now, use the context property in both for storing arbitrary metadata.
        // Base columns config comes from the server side, so we need to suppress the warning
        suppressPropertyNamesCheck: true,

        suppressDragLeaveHidesColumns: true,
        defaultColDef: {
          editable: false,
          sortable: true,
          filter: true,
          floatingFilter: true,
          resizable: true,
          menuTabs: ['generalMenuTab'],
        },
        statusBar: { statusPanels: [{ statusPanel: 'agAggregationComponent' }] },
        columnTypes: {
          maintainOption: {
            valueFormatter: params => {
              if (!params.value) {
                return this.t('scenarioInputPage.attributes.maintainOptions.doNothing');
              }
              const options = params.value.split(' | ');
              return options
                .map(option =>
                  this.t(`scenarioInputPage.attributes.maintainOptions.${option || 'doNothing'}`)
                )
                .join(' | ');
            },
          },
          float: {
            valueFormatter: params => this.formatFloatValue(params.colDef, params.value),
            filterValueGetter: params => {
              const colId = params.column.colId;
              const value = params.data[colId];
              return this.formatFloatValue(colId, value);
            },
          },
          floatThreeDecimals: {
            valueFormatter: params => {
              return this.formatFloatValue(params.colDef, params.value, 'numberWithThreeDecimals');
            },
            filterValueGetter: this.numberWithThreeDecimalsGetter,
            valueGetter: this.numberWithThreeDecimalsGetter,
          },
          marginPercentage: {
            valueFormatter: params => {
              const formattedMargin = Number(params.value * 100)
                .toFixed(0)
                .toLocaleString(this.currentNumericLocale);
              return `${formattedMargin}%`;
            },
            filterValueGetter: params => {
              const colId = params.column.colId;
              const value = params.data[colId];
              const formattedMargin = Number(value * 100)
                .toFixed(0)
                .toLocaleString(this.currentNumericLocale);
              return `${formattedMargin}%`;
            },
            valueGetter: params => {
              const colId = params.column.colId;
              const value = params.data[colId];
              return value;
            },
          },
        },
      };
    },

    currentNumericLocale() {
      return this.contextStore.getCurrentNumericLocale;
    },

    isTableLoading() {
      return this.reportingStore.loadingSubsection.productTable;
    },

    tableColumnState() {
      if (!this.gridApi) return [];
      return this.gridApi.getColumnState();
    },

    requiredCurrency() {
      return this.t(`currencies.${this.contextStore.getCurrentNumericLocale}`);
    },

    columnDefs() {
      // If the report is not loaded yet or got marked as stale, return empty array
      if (isEmpty(this.columnsConfig) || this.reportingStore.isReportDataStale) {
        return [];
      }
      const sortedColumns = sortBy(
        flatten(
          this.columnsConfig.map(column => {
            // some columns have dynamic headerName generated on BE
            let columnName =
              column.headerName ||
              this.tkey(`table.${column.field}`, { currency: this.requiredCurrency });

            if (column.isChainCluster) {
              const headerSuffix = this.tkey(`table.${column.headerSuffix}`);
              columnName += ` - ${headerSuffix}`;
            }

            let isProductTypeConfig = {};
            if (column.field === fieldEnum.productType) {
              isProductTypeConfig = {
                cellClass: 'justified-center border-left limit-focus-padding chip-cell',
                cellRenderer: this.productTypeCellRenderer,
                valueGetter: this.productTypeValueGetter,
                filterParams: { valueFormatter: params => params.value },
              };
            }

            // if no comparison is selected - do not add 'C' prefix to the column header name
            const baseHeaderName = isEmpty(this.reportingStore.comparisonIds)
              ? columnName
              : `${this.t('reportingPage.menuHeaders.baseShort')} - ${columnName}`;

            const baselineColumn = {
              width: 150,
              ...column,
              ...isProductTypeConfig,
              headerName: baseHeaderName,
            };

            const columns = [baselineColumn];

            if (!column.skipComparisonSplit) {
              if (!isEmpty(this.reportingStore.comparisonIds)) {
                columns.push({
                  ...baselineColumn,
                  field: `${baselineColumn.field}-comparison`,
                  headerName: `${this.t(
                    'reportingPage.menuHeaders.comparisonShort'
                  )} - ${columnName}`,
                });
              }
              if (!isEmpty(this.reportingStore.comparison2Ids)) {
                columns.push({
                  ...baselineColumn,
                  field: `${baselineColumn.field}-comparison2`,
                  headerName: `${this.t(
                    'reportingPage.menuHeaders.comparison2Short'
                  )} - ${columnName}`,
                });
              }
            }

            // If this is a column that compares data between comparison and base checkpoints,
            // add it dynamically if applicable
            if (column.comparisonWithBase) {
              const columnNameEnding = column.comparisonWithBaseAsPercentage ? '%' : '';

              if (!isEmpty(this.reportingStore.comparisonIds)) {
                columns.push({
                  ...baselineColumn,
                  field: `${baselineColumn.field}-comparison-vs-base`,
                  headerName: `${this.t(
                    'reportingPage.menuHeaders.comparisonVsBase'
                  )} - ${columnName} ${this.t(
                    'reportingPage.menuHeaders.change'
                  )} ${columnNameEnding}`,
                });
              }
              if (!isEmpty(this.reportingStore.comparison2Ids)) {
                columns.push({
                  ...baselineColumn,
                  field: `${baselineColumn.field}-comparison2-vs-base`,
                  headerName: `${this.t(
                    'reportingPage.menuHeaders.comparison2VsBase'
                  )} - ${columnName} ${columnNameEnding}`,
                });
              }
            }

            return columns;
          })
        ),
        ['order', 'isClientOverride']
      );
      let groups = [];
      const groupedColumns = groupBy(sortedColumns, 'parent');
      forEach(groupedColumns, (value, key, index) => {
        groupedColumns[key][0].headerClass = 'divider-left';
        groupedColumns[key][0].cellClass = 'divider-left';
        groups.push({
          headerName: this.tkey(`table.${key}`),
          headerClass: index === 0 ? '' : 'divider-left',
          groupId: key,
          children: groupedColumns[key],
        });
      });
      groups = sortBy(groups, group => groupsOrderMap[group.groupId]);

      return groups;
    },
  },

  watch: {
    'reportingStore.reportsGenerated': function () {
      this.fetchData();
    },
  },

  created() {
    this.excelStyles = [
      {
        id: 'overviewHeaderText',
        font: { bold: true },
      },
      {
        id: 'filtersWarning',
        font: { bold: true },
        interior: {
          color: '#eeee66',
          pattern: 'Solid',
        },
      },
    ];
    this.fetchData();

    this.sideBar = {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: '',
          labelKey: 'columns',
          iconKey: 'close', // Icon acts as close button
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressPivots: true,
            suppressRowGroups: true,
            suppressValues: true,
          },
          width: 400,
        },
        {
          id: 'layouts',
          labelDefault: '',
          labelKey: 'layouts',
          iconKey: 'close', // Icon acts as close button
          toolPanel: 'ProductLayouts',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressPivots: true,
            suppressRowGroups: true,
            suppressValues: true,
            tableColumnState: this.tableColumnState,
            applyColumnLayout: layout => this.applyColumnLayout(layout),
            saveLayout: params => this.saveLayout(params),
          },
          width: 400,
        },
      ],
      hiddenByDefault: true,
    };
  },

  methods: {
    // Get a map of the custom attribute names and ids
    // this allows the layouts to be used across workpackages with the same attribute names
    getCustomAttMap(isLoading = false) {
      const allColumns = this.gridApi.getColumns();
      const customAttributeMap = {};
      if (allColumns) {
        allColumns.forEach(attribute => {
          const attId = attribute.colId;
          const name = attribute.colDef.headerName;
          if (isLoading) {
            // When loading the name needs to be mapped to an id
            customAttributeMap[name] = attId;
          } else if (get(attribute, 'userProvidedColDef.parent') === 'customAttributes') {
            // When saving the id needs to be mapped to the name
            customAttributeMap[attId] = name;
          }
        });
      }
      return customAttributeMap;
    },

    // Convert custom attribute ids to names to be stored
    // This allows them to be used by other wps
    formatLayout(layout, isLoading = false) {
      const customAttributesMap = this.getCustomAttMap(isLoading);
      return layout.map(col => {
        if (
          customAttributesMap[col.colId] &&
          (!isLoading || (isLoading && col.isCustomAttribute))
        ) {
          col.colId = customAttributesMap[col.colId];
          // We store that it was originally a custom att so we can convert the id back later
          if (!isLoading) {
            col.isCustomAttribute = true;
          }
        }
        return col;
      });
    },
    saveLayout({ name, closeModal }) {
      // Get the current layout
      let layout = this.gridApi.getColumnState();
      layout = this.formatLayout(layout);

      this.reportingStore
        .createLayout({
          name,
          layout,
          table: 'ProductReport',
        })
        .then(() => closeModal());
    },

    async fetchData() {
      const response = await this.reportingStore.fetchReportingData({
        section: this.section,
        reportArea: 'table',
      });
      this.tableData = response.data;
      this.columnsConfig = response.columnsConfig;
      this.overviewData = response.overviewData;
    },

    onGridReady(params) {
      this.gridApi = params.api;
    },

    toggleSideBar(selectedToolPanel) {
      const openToolPanel = this.gridApi.getOpenedToolPanel();
      // If the selected toolbar is not open, open it
      if (!openToolPanel || openToolPanel !== selectedToolPanel) {
        this.gridApi.setSideBarVisible(true);
        this.gridApi.openToolPanel(selectedToolPanel);
      } else if (openToolPanel === selectedToolPanel) {
        // If the selected tool panel is already open, close it
        this.gridApi.closeToolPanel();
        this.gridApi.setSideBarVisible(false);
      }
    },

    getOverviewTabContents(includeFiltersInfo = false) {
      const columns = [
        { key: 'workpackage', type: 'String', property: 'name' },
        { key: 'scenario', type: 'String', property: 'name' },
        { key: 'cluster', type: 'String', property: 'name' },
        { key: 'storeClass', type: 'String', property: 'name' },
        { key: 'description', type: 'String' },
        { key: 'checkpoint', type: 'String', property: 'name' },
        { key: 'spacebreak', type: 'String', property: 'name' },
        { key: 'storeCount', type: 'String' },
      ];

      const overviewColumns = keyBy(columns, 'key');
      const overviewKeys = Object.keys(overviewColumns);
      const headerCells = overviewKeys.map(c => ({
        styleId: 'overviewHeaderText',
        data: {
          value: this.t(`reportingPage.overviewHeaders.${c}`),
          type: overviewColumns[c].type,
        },
      }));

      const overviewData = this.overviewData.map(o => {
        const cells = overviewKeys.map(c => {
          const value = has(overviewColumns[c], 'property')
            ? o[c][overviewColumns[c].property]
            : o[c];
          return { data: { value, type: overviewColumns[c].type } };
        });
        return { cells };
      });
      const filtersInfo = {
        cells: [
          {
            styleId: 'filtersWarning',
            mergeAcross: columns.length - 1,
            data: { value: this.t('reportingPage.exportFilterMessage'), type: 'String' },
          },
        ],
      };
      const prependContent = [{ cells: headerCells }, ...overviewData];
      if (includeFiltersInfo) prependContent.splice(0, 0, filtersInfo);

      return {
        prependContent,
        sheetName: this.t('reportingPage.overviewTab'),
        // skip regular column header and group header
        skipColumnGroupHeaders: true,
        processHeaderCallback: () => '',
        processGroupHeaderCallback: () => '',
      };
    },

    async exportToExcel() {
      const hasFiltersApplied = !isEmpty(this.gridApi.getColumnFilterModel());
      const filterColumn = this.gridApi.getColumnDefs()[0].children[0].field; // first column used as dummy filter
      // Filter out ag-grid data for the overview tab
      const agGridFilter = {
        [filterColumn]: {
          type: 'set',
          values: [],
        },
      };
      this.gridApi.setColumnFilterModel(agGridFilter);

      this.gridApi.onFilterChanged();
      const spreadsheets = [
        this.gridApi.getSheetDataForExcel(this.getOverviewTabContents(hasFiltersApplied)),
      ];
      this.gridApi.setColumnFilterModel(null);
      this.gridApi.onFilterChanged();
      spreadsheets.push(
        this.gridApi.getSheetDataForExcel({ sheetName: this.t('reportingPage.reportTab') })
      );

      const fileName = `${
        hasFiltersApplied
          ? this.t('reportingPage.exportFilteredFileName')
          : this.t('reportingPage.exportFileName')
      } - ${dayjs().format(this.contextStore.getDateFormats.longWithFullTime)}`;

      this.gridApi.exportMultipleSheetsAsExcel({
        data: spreadsheets,
        author: 'AO',
        fileName,
      });
    },

    async excelExport() {
      this.isExporting = true;
      const hasFiltersApplied = !isEmpty(this.gridApi.getColumnFilterModel());
      if (hasFiltersApplied) {
        this.isConfirmDialogOpen = true;
      } else {
        await this.exportToExcel();
      }
      this.isExporting = false;
    },

    formatFloatValue(colDef, value, format = 'float') {
      if (colDef.isArray && isArray(value) && !isEmpty(value)) {
        return value
          .map(v =>
            agGridUtils.utils.valueIsFalsy({ value: v })
              ? colDef.noValue || colDef.noBaseComparisonValue || ''
              : this.formatNumber({ number: v, format })
          )
          .join(' | ');
      }
      return agGridUtils.utils.valueIsFalsy({ value })
        ? colDef.noValue || colDef.noBaseComparisonValue || ''
        : this.formatNumber({ number: value, format });
    },

    // Applies the received column state to the grid
    applyColumnLayout(layoutColumnsState) {
      const currentColumnsState = this.gridApi.getColumnState();
      layoutColumnsState = this.formatLayout(layoutColumnsState, true);

      // hide columns that are currently visible but not presented in the layout to apply
      const columnsToHide = differenceBy(currentColumnsState, layoutColumnsState, 'colId');
      columnsToHide.forEach(column => {
        column.hide = true;
      });

      this.gridApi.applyColumnState({
        state: [...layoutColumnsState, ...columnsToHide],
        applyOrder: true,
      });
      this.snackbarStore.showSuccess(this.tkey('layoutApplied'));

      // get columns in layout that are missing in the current columnsConfig
      const missingColumns = differenceBy(layoutColumnsState, currentColumnsState, 'colId');
      if (!isEmpty(missingColumns)) {
        const content =
          this.tkey('layoutApplied') +
          this.tkey('columnsMissing', { count: missingColumns.length });
        this.snackbarStore.showWarning(content);
      }
    },

    numberWithThreeDecimalsGetter(params) {
      const colId = params.column.colId;
      const value = params.data[colId];

      return this.isExporting
        ? parseFloat(value)
        : this.formatFloatValue(colId, value, 'numberWithThreeDecimals');
    },

    closeConfirmExportDialog() {
      this.isConfirmDialogOpen = false;
    },

    productTypeCellRenderer(params) {
      // The reporting api returns the fields as an array
      // that's why we need the method head
      const product = { ...params.data, productType: head(params.data.productType) };
      const productType = product.productType;
      return `
        <span class="rtls-chip ${productType.style}">
          <span class="rtls-chip__content">
            ${params.value}
          </span>
        </span>
      `;
    },
    productTypeValueGetter(params) {
      // The reporting api returns the fields as an array
      // that's why we need the method head
      const product = { ...params.data, productType: head(params.data.productType) };
      const productType = product.productType;
      return this.getProductTypeDisplayString(productType.translation, this.tkey);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.chart-area {
  & .header {
    justify-content: space-between;
  }
}

:deep(.v-btn) {
  &:not(:last-of-type) {
    margin-right: 30px;
  }
}

:deep(.baseline-row) {
  background-color: lighten($baseline-colour, 60%) !important;
}

:deep(.comparison-row) {
  background-color: lighten($comparison-colour, 30%) !important;
}

:deep(.comparison2-row) {
  background-color: lighten($comparison2-colour, 30%) !important;
}

:deep(.ag-side-button) {
  display: none;
}

:deep(.ag-theme-alpine) {
  .ag-row {
    font-size: 11px !important;
  }

  .ag-cell-wrapper {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-left: 0 !important;
  }

  .ag-row-group-indent {
    &-1 {
      padding-left: 15px !important;
    }

    &-2 {
      padding-left: 25px !important;
    }

    &-3 {
      padding-left: 35px !important;
    }

    &-4 {
      padding-left: 45px !important;
    }
  }

  .ag-side-buttons {
    padding-top: 0;
    position: absolute;
    z-index: 1;

    .ag-side-button {
      &:not(.ag-selected) {
        display: none;
      }

      .ag-side-button-button {
        border-left: none;
        min-height: unset;
        padding: 8px 10px 8px 0;
      }

      .ag-side-button-icon-wrapper {
        color: $assortment-primary-colour;
      }
    }
  }

  .ag-column-select-header {
    padding-right: 30px !important;

    .ag-column-select-header-filter-wrapper:before {
      right: 30px !important;
    }
  }

  .ag-status-bar {
    .ag-status-name-value {
      line-height: 1;
      padding: 10px 6px 0;
    }
  }
}

.no-data {
  font-size: 1.4rem;
}
</style>
